import { LaunchUniversal, ShowOutlined } from '@duxton-web/icons';
import {
  Button,
  Flex,
  Modal,
  Tooltip,
  Typography,
} from '@duxton-web/ui';
import { targetUserTypes } from 'data/trident-types';
import i18n from 'i18n-js';
import _ from 'lodash';
import React, { useState } from 'react';

import SegPInsightsWidget from 'components/form/trident-campaigns/SegPInsightsWidget';
import { AudienceSourceType } from 'omega-rtc/types/ts/content-config';
import {
  getAudienceTableUrl,
  SegmentSourceParts,
  splitSegmentSource,
} from 'omega-rtc/utils/audience-utils';
import { getSegmentUrl } from 'omega-rtc/views/trident-campaigns/review/campaign-analysis';

const linkText = (key) =>
  i18n.t(`ucd_content_config.audiences.source_links.${key}`);

export interface AudienceSourceLinkInfo {
  cdpUrl: string;
  hubbleUrl: string;
  segment: SegmentSourceParts | null;
}

// getAudienceSourceLinks is the single rule set for where an Audience source
// links to. Segments are always picked as PAX by the Audience form's
// SegPWidget, so the CDP link uses the same user type. A table source that
// getAudienceTableUrl rejects yields no Hubble link rather than a broken one.
// SegP namespaces and the Hubble dataset are global, so scope does not matter.
export const getAudienceSourceLinks = (
  type: AudienceSourceType | undefined,
  source: string | undefined,
): AudienceSourceLinkInfo => {
  const value = _.toString(source).trim();
  if (value && type === AudienceSourceType.Segment) {
    return {
      cdpUrl: getSegmentUrl(targetUserTypes.pax, value),
      hubbleUrl: '',
      segment: splitSegmentSource(value),
    };
  }
  if (value && type === AudienceSourceType.Table) {
    return {
      cdpUrl: '',
      hubbleUrl: getAudienceTableUrl(value),
      segment: null,
    };
  }
  return { cdpUrl: '', hubbleUrl: '', segment: null };
};

interface SegmentPreviewModalProps {
  onClose: () => void;
  open: boolean;
  segment: SegmentSourceParts;
}

const SegmentPreviewModal = ({
  onClose,
  open,
  segment,
}: SegmentPreviewModalProps) => (
  <Modal
    title={linkText('segment_insights')}
    open={open}
    onCancel={onClose}
    footer={null}
    width={800}
    styles={{
      body: { padding: '20px 0' },
    }}
  >
    {open && (
      <SegPInsightsWidget
        segmentID={segment.segmentName}
        namespace={segment.namespace}
      />
    )}
  </Modal>
);

interface AudienceSourceLinksProps {
  source?: string;
  type?: AudienceSourceType;
}

// AudienceSourceLinks renders the Audience detail's source actions with the
// same markup as SmartCampaignOverviewCard.
export const AudienceSourceLinks = ({ source, type }: AudienceSourceLinksProps) => {
  const [showPreview, setShowPreview] = useState(false);
  const { cdpUrl, hubbleUrl, segment } = getAudienceSourceLinks(type, source);

  if (!cdpUrl && !hubbleUrl) {
    return null;
  }

  return (
    <Flex gap={16} wrap="wrap" className="cc-audience-source-links">
      {segment && (
        <Typography.Link
          onClick={() => setShowPreview(true)}
          style={{ cursor: 'pointer' }}
        >
          {linkText('preview_segment')} <ShowOutlined />
        </Typography.Link>
      )}
      {cdpUrl && (
        <Typography.Link
          href={cdpUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          {linkText('open_cdp')} <LaunchUniversal />
        </Typography.Link>
      )}
      {hubbleUrl && (
        <Typography.Link
          href={hubbleUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          {linkText('open_hubble')} <LaunchUniversal />
        </Typography.Link>
      )}
      {segment && (
        <SegmentPreviewModal
          open={showPreview}
          onClose={() => setShowPreview(false)}
          segment={segment}
        />
      )}
    </Flex>
  );
};

const stopRowClick = (event: React.SyntheticEvent) => event.stopPropagation();

// AudienceSourceCell renders the Audience list's Source value. The list row
// opens the detail on click, and React events bubble through the Modal portal,
// so the link, the eye icon and the modal swallow their clicks while the rest
// of the cell still opens the row.
export const AudienceSourceCell = ({ source, type }: AudienceSourceLinksProps) => {
  const [showPreview, setShowPreview] = useState(false);
  const { cdpUrl, hubbleUrl, segment } = getAudienceSourceLinks(type, source);
  const value = _.toString(source).trim();
  const url = cdpUrl || hubbleUrl;

  return (
    <Flex align="center" gap={6} style={{ minWidth: 0 }}>
      {url ? (
        <Typography.Link
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          ellipsis
          onClick={stopRowClick}
        >
          {value}
        </Typography.Link>
      ) : (
        <Typography.Body level={2}>{value || '—'}</Typography.Body>
      )}
      {segment && (
        <React.Fragment>
          <Tooltip title={linkText('preview_segment')}>
            <Button
              type="text"
              size="small"
              aria-label={linkText('preview_segment')}
              icon={<ShowOutlined />}
              style={{ flexShrink: 0 }}
              onClick={(event) => {
                stopRowClick(event);
                setShowPreview(true);
              }}
            />
          </Tooltip>
          <span onClick={stopRowClick}>
            <SegmentPreviewModal
              open={showPreview}
              onClose={() => setShowPreview(false)}
              segment={segment}
            />
          </span>
        </React.Fragment>
      )}
    </Flex>
  );
};
